
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { PriceIndexDateRangeObject } from '@/store/modules/Indices/PriceIndexDateRangeModule';
  import { IndexTypeViewTypeObject } from '@/store/modules/Indices/IndicesAssetsModule';
  import { IndicesType } from '@/store/enums/TypeOfIndicesEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'date-range-setup-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('priceIndexDateRangeUpdating'), [
          translate('indices'),
        ]);
      });

      const MIN_YEAR_FOR_DATE_RANGE = 2000;
      const currentYear = new Date().getFullYear();
      const yearOptions: number[] = Array.from(
        { length: currentYear - MIN_YEAR_FOR_DATE_RANGE + 1 },
        (_, i) => MIN_YEAR_FOR_DATE_RANGE + i
      );

      const intervals = ref<{ id: number; key: string }[]>([]);
      const indexTypes = ref<
        { id: number; slug: string; name_ar: string; name_en: string }[]
      >([]);

      const [itemRes, intervalsRes, indexTypesRes, priceVTs, volumeVTs] =
        await Promise.all([
          store.dispatch(Actions.GET_PRICE_INDEX_DATE_RANGE, route.params.id),
          store.dispatch(Actions.GET_INDEX_INTERVALS),
          store.dispatch(Actions.GET_INDEX_TYPES),
          store.dispatch(
            Actions.GET_ALL_INDEX_TYPE_VIEW_TYPES,
            IndicesType.PRICE_INDEX.id
          ),
          store.dispatch(
            Actions.GET_ALL_INDEX_TYPE_VIEW_TYPES,
            IndicesType.VOLUME_INDEX.id
          ),
        ]);

      intervals.value = intervalsRes?.data?.data ?? intervalsRes?.data ?? [];
      indexTypes.value = indexTypesRes?.data?.data ?? indexTypesRes?.data ?? [];

      const viewTypesByIndexType = ref<
        Record<number, IndexTypeViewTypeObject[]>
      >({
        [IndicesType.PRICE_INDEX.id]: priceVTs ?? [],
        [IndicesType.VOLUME_INDEX.id]: volumeVTs ?? [],
      });

      // eslint-disable-next-line no-console
      console.log('[viewTypesByIndexType]', viewTypesByIndexType.value);

      const item = ref<PriceIndexDateRangeObject>(itemRes.data.data);
      const supportedIntervals = Array.isArray(item.value?.supportedIntervals)
        ? item.value.supportedIntervals
        : [];

      const checkboxKey = (intervalId: number, viewTypeId: number) =>
        `${intervalId}_${viewTypeId}`;

      const checkedIntervalViewTypes = ref<Record<string, boolean>>(
        Object.fromEntries(
          supportedIntervals
            .filter((interval) => interval.viewTypeId !== null)
            .map((interval) => [
              checkboxKey(interval.intervalId, interval.viewTypeId as number),
              true,
            ])
        )
      );

      const currentViewTypes = computed(
        () => viewTypesByIndexType.value[Number(item.value.typeId)] ?? []
      );

      const isChecked = (intervalId: number, viewTypeId: number) =>
        !!checkedIntervalViewTypes.value[checkboxKey(intervalId, viewTypeId)];

      const onTypeIdChange = () => {
        checkedIntervalViewTypes.value = {};
      };

      const onIntervalViewTypeToggle = (
        intervalId: number,
        viewTypeId: number,
        checked: boolean
      ) => {
        const key = checkboxKey(intervalId, viewTypeId);
        // eslint-disable-next-line no-console
        console.log('[toggle] key=%s checked=%s before=%o', key, checked, {
          ...checkedIntervalViewTypes.value,
        });
        checkedIntervalViewTypes.value = {
          ...checkedIntervalViewTypes.value,
          [key]: checked,
        };
        // eslint-disable-next-line no-console
        console.log('[toggle] after=%o', { ...checkedIntervalViewTypes.value });
      };

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        typeId: Yup.number()
          .required(() => translate('TYPE_ID_IS_REQUIRED_FIELD'))
          .label('Index Type'),
        titleAr: Yup.string()
          .required(() => translate('TITLE_AR_IS_REQUIRED_FIELD'))
          .label('Title Ar'),
        titleEn: Yup.string()
          .required(() => translate('TITLE_EN_IS_REQUIRED_FIELD'))
          .label('Title En'),
        startYear: Yup.number()
          .required(() => translate('START_YEAR_IS_REQUIRED_FIELD'))
          .integer()
          .min(1900)
          .label('Start Year'),
        endYear: Yup.number()
          .required(() => translate('END_YEAR_IS_REQUIRED_FIELD'))
          .integer()
          .min(1900)
          .label('End Year'),
        sort: Yup.number()
          .required(() => translate('SORT_IS_REQUIRED_FIELD'))
          .integer()
          .min(0)
          .label('Sort'),
      });

      const onSubmitUpdate = async (values) => {
        const supportedIntervalsPayload = Object.entries(
          checkedIntervalViewTypes.value
        )
          .filter(([, checked]) => checked)
          .map(([key]) => {
            const [intervalId, viewTypeId] = key.split('_').map(Number);
            return { intervalId, viewTypeId };
          });

        if (!supportedIntervalsPayload.length) {
          Swal.fire({
            text: translate('SUPPORTED_INTERVALS_IS_REQUIRED_FIELD'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
          });
          return;
        }

        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = {
          typeId: Number(values.typeId),
          titleAr: values.titleAr,
          titleEn: values.titleEn,
          startYear: Number(values.startYear),
          endYear: Number(values.endYear),
          sort: Number(values.sort),
          supportedIntervals: supportedIntervalsPayload,
          isDefault: item.value.isDefault,
        };

        await store.dispatch(Actions.UPDATE_PRICE_INDEX_DATE_RANGE, {
          id: item.value?.id,
          data: payload,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_INDEX_DATE_RANGE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'date-range-setup-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        item,
        intervals,
        indexTypes,
        currentViewTypes,
        isChecked,
        onTypeIdChange,
        onIntervalViewTypeToggle,
        can,
        currentYear,
        yearOptions,
      };
    },
  });
